import { Box } from "@mui/material";
import React, { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  AddCastRow,
  DuplicateCastRow,
  GetCulturesList,
  GetSelectedTitle,
  GetTitleSelectedCulture,
  RemoveCastRow,
  SetUpdateField,
  UpdateCastRows,
  UpdateTitleLocalizedMeta,
} from "../../Redux/Reducers/redux-slice-bundle-maker";
import FormSection from "../../Components/Forms/FormSection/form-section";
import MetadataInputIndex from "../../Components/MetadataInput/metadata-input-index";
import { DbMetadataFieldInfos } from "../../Core/Api/Types/db-metadata-field-infos";
import { BundleMetadataValidationStatus } from "../../Utils/Metadata/utils-metadata-validation";
import { useDispatch } from "react-redux";
import { BaseMetadataType } from "../../Core/Api/Types/base-metadata-type";
import { EnrichedMetadata } from "../../Core/Api/Types/enriched-metadata";
import GlobalConst from "../../Core/Utils/Constants/globals";
import { GetMetadataByTagName } from "../../Core/Utils/asset-object-utils";
import { useTranslation } from "react-i18next";

type fieldInputTitleBundlemakerProps = {
  m: EnrichedMetadata;
  index: number;
  triggerChange: number;
  currentTitleMetadataValidation: any;
  selectedEpisode: number | null;
};

const FieldInputTitleBundlemaker: React.FC<fieldInputTitleBundlemakerProps> = (props) => {
  const { t } = useTranslation();
  const { m, triggerChange, index, currentTitleMetadataValidation, selectedEpisode } = props;
  const dispatch = useDispatch();

  const cultures = useSelector(GetCulturesList);
  const titleSelectedCulture =
    useSelector(GetTitleSelectedCulture) ?? cultures.find((x) => x.id === GlobalConst.WORLD_CULTURE_ID);

  const title = useSelector(GetSelectedTitle);
  const titleType = useMemo(() => GetMetadataByTagName(title, "titleType")?.value ?? "Movie", []);

  const handleMetadataUpdate = (
    id: number,
    value: any,
    valueIndex?: number,
    pushAndReplaceWhereCultureIds?: number[]
  ) => {
    dispatch(UpdateTitleLocalizedMeta({ id, value, valueIndex, pushAndReplaceWhereCultureIds }));
    dispatch(
      SetUpdateField({
        field: m.field?.baseType === BaseMetadataType.DbCastsMetadataValueInfos ? `title_cast` : `title_${index}`,
      })
    );
  };

  const onRemoveRow = useCallback(
    (index) => {
      m.field?.baseType === BaseMetadataType.DbCastsMetadataValueInfos && dispatch(RemoveCastRow({ index }));
    },
    [m.field?.baseType, index]
  );
  const onAddRow = useCallback(() => {
    m.field?.baseType === BaseMetadataType.DbCastsMetadataValueInfos && dispatch(AddCastRow({}));
  }, [m.field?.baseType, index]);
  const onDuplicateRows = useCallback(
    (indexes) => {
      m.field?.baseType === BaseMetadataType.DbCastsMetadataValueInfos && dispatch(DuplicateCastRow({ indexes }));
    },
    [m.field?.baseType, index]
  );
  const onUpdateAllRows = useCallback(
    (value: any, field: string) => {
      m.field?.baseType === BaseMetadataType.DbCastsMetadataValueInfos &&
        dispatch(UpdateCastRows({ value, index: -1, field }));
    },
    [m.field?.baseType]
  );
  const onUpdateRowsBelow = useCallback(
    (value: any, index: number, field: string) => {
      m.field?.baseType === BaseMetadataType.DbCastsMetadataValueInfos &&
        dispatch(UpdateCastRows({ value, index, field }));
    },
    [m.field?.baseType]
  );

  const _render = useMemo(() => {
    if (!titleSelectedCulture) return <></>;

    return (
      <FormSection
        title={`${m.field?.label!}`}
        sx={{
          marginBottom: "20px",
          width:
            m.field?.baseType === BaseMetadataType.DbSynopsesMetadataValueInfos ||
            m.field?.baseType === BaseMetadataType.DbCastsMetadataValueInfos
              ? "100%"
              : "49%",
        }}
        status={
          currentTitleMetadataValidation.find((x) => x.fieldId === m.field?.id)?.status ===
          BundleMetadataValidationStatus.Ok
            ? "complete"
            : m.usage?.isMandatory
            ? "warning"
            : "todo"
        }
        statusText={
          currentTitleMetadataValidation.find((x) => x.fieldId === m.field?.id)?.status ===
          BundleMetadataValidationStatus.Ok
            ? t("common:configured")
            : m.usage?.isMandatory
            ? t("common:configurationRequired")
            : t("form:statusText.configurationIsOptional")
        }
        key={`metadata-${m.field?.id}`}
      >
        <Box display="flex" alignItems="baseline" alignContent="center" justifyContent="space-between" width="100%">
          <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
            <MetadataInputIndex
              onRemoveRow={onRemoveRow}
              onAddRow={onAddRow}
              onDuplicateRows={onDuplicateRows}
              onUpdateAllRows={onUpdateAllRows}
              onUpdateRowsBelow={onUpdateRowsBelow}
              availableLanguages={
                titleSelectedCulture.languages &&
                titleSelectedCulture.languages.length > 1 &&
                titleSelectedCulture.id !== GlobalConst.WORLD_CULTURE_ID
                  ? titleSelectedCulture.languages
                  : undefined
              }
              key={index}
              data={m.field! as DbMetadataFieldInfos}
              value={m.value?.value}
              predefinedValues={true}
              cultureId={titleSelectedCulture.id ?? GlobalConst.WORLD_CULTURE_ID}
              epNb={selectedEpisode}
              titleType={titleType}
              onChange={(v, fieldId, valueIndex, pushAndReplaceWhereCultureId) =>
                handleMetadataUpdate(fieldId, v, valueIndex, pushAndReplaceWhereCultureId as number[])
              }
            />
          </Box>
        </Box>
      </FormSection>
    );
  }, [triggerChange, titleSelectedCulture?.id, selectedEpisode]);

  return _render;
};

export default FieldInputTitleBundlemaker;
